import React, { useCallback, useEffect, useMemo, useState } from "react";

import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import { EntityType, EntityTypesSupportingSavedFilters } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import { FilterOptionValue } from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import FilterModel, {
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import FilterOperatorAction from "@app/types/FilterOperatorAction";
import useAnalytics from "@app/util/contexts/useAnalytics";
import loggingService from "@app/util/logging";

import FieldColumn from "../../FieldColumn";
import FilterSelector from "../../FilterSelector";

import styles from "./FilterDropdown.module.scss";
import CreateFilterModal from "./filterModals/CreateFilterModal";
import FilterState from "./FilterState";
import Footer from "./Footer";

interface Props {
  checkInFieldsVisibility: boolean;
  context: string;
  entityType: EntityType;
  fieldModel: IFieldModel;
  filteredFields: IField[];
  filterModel: Partial<FilterModel>;
  filterOperators: FilterOperatorAction;
  filterOptions?: Partial<Record<FilterOption, FilterOptionValue>>;
  hiddenFields?: IField[];
  hideSavedFilters?: boolean;
  implicitFilterFields?: IField[];
  onChange?: (
    callback: (previousFilterModel: Partial<FilterModel>) => Partial<FilterModel>
  ) => void;
  selectedFilter?: SavedFilter;
  showColumnGroups?: boolean;
}

const FilterDropdown: React.FC<Props> = ({
  checkInFieldsVisibility,
  context,
  entityType,
  fieldModel,
  filteredFields,
  filterModel,
  filterOperators,
  filterOptions,
  hiddenFields,
  hideSavedFilters,
  implicitFilterFields,
  onChange,
  selectedFilter,
  showColumnGroups,
}) => {
  const analyticIssuer = useAnalytics();

  const [createFilterVisible, showCreateFilter, hideCreateFilter] = useBoolean();

  const [newFilterShown, setNewFilterShown] = useState<boolean>(filteredFields.length === 0);
  useEffect(() => {
    setNewFilterShown(filteredFields.length === 0);
  }, [filteredFields]);

  const handleChangeFilter = useDynamicCallback((field: IField, condition: SimpleCondition) => {
    onChange?.((filterModel) => {
      const updatedFilterModel = { ...filterModel };
      // This removal is needed to guarantee that the new field will be added to the end of the list.
      // The only reason it could appear not at the end was when such field has been added once and then
      // removed. It was kept with an undefined value is such case, preserving its previous position.
      if (
        field.filterName in updatedFilterModel &&
        updatedFilterModel[field.filterName] === undefined
      ) {
        delete updatedFilterModel[field.filterName];
      }
      updatedFilterModel[field.filterName] = condition;
      loggingService.debug("onChange filter ", { new: updatedFilterModel, old: filterModel });
      return updatedFilterModel;
    });
  });

  const handleRemoveFilter = useDynamicCallback((field: IField) => {
    onChange?.((filterModel) => ({ ...filterModel, [field.filterName]: undefined }));
  });

  const handleAddFilterClick = useDynamicCallback(() => {
    setNewFilterShown(true);
    analyticIssuer?.clicked(["Add Filter"]);
  });

  const handleChangeField = useCallback(
    (oldField, newField, newCondition) => {
      handleChangeFilter(newField, newCondition);
    },
    [handleChangeFilter]
  );

  const implicitFilterFieldNames: Set<string> = useMemo(
    () => new Set(implicitFilterFields?.map(({ name }) => name)),
    [implicitFilterFields]
  );

  return (
    <>
      <div className={styles.container}>
        <FilterState
          entityType={entityType as Exclude<EntityTypesSupportingSavedFilters, EntityType.PIN>}
          hideSavedFilters={hideSavedFilters}
          implicitFilterFields={implicitFilterFields}
          onCreate={showCreateFilter}
        />
        <div className={styles.filtersGrid}>
          {filteredFields.map((field, index) => (
            <FilterSelector
              checkInFieldsVisibility={checkInFieldsVisibility}
              context={context}
              entityType={entityType}
              field={field}
              fieldModel={fieldModel}
              filterModel={filterModel}
              filterOperators={filterOperators}
              filterOptions={filterOptions}
              firstFilter={index === 0}
              hiddenFields={hiddenFields}
              isImplicit={implicitFilterFieldNames.has(field.name)}
              key={field.name}
              onChange={handleChangeFilter}
              onRemove={handleRemoveFilter}
              showColumnGroups={showColumnGroups}
            />
          ))}
          {newFilterShown && (
            <FieldColumn
              checkInFieldsVisibility={checkInFieldsVisibility}
              className={styles.fieldName}
              context={context}
              entityType={entityType}
              fieldModel={fieldModel}
              filterModel={filterModel}
              filterOperators={filterOperators}
              firstFilter={filteredFields.length === 0}
              hiddenFields={hiddenFields}
              onChange={handleChangeField}
              showColumnGroups={showColumnGroups}
            />
          )}
        </div>
        <Footer
          addDisabled={newFilterShown}
          className={styles.footer}
          hideSavedFilters={hideSavedFilters}
          implicitFilterFields={implicitFilterFields}
          onAdd={handleAddFilterClick}
          onCreateSavedFilter={showCreateFilter}
          selectedFilter={selectedFilter}
        />
      </div>
      <CreateFilterModal
        entityType={entityType as Exclude<EntityTypesSupportingSavedFilters, EntityType.PIN>}
        implicitFilterFields={implicitFilterFields}
        onHide={hideCreateFilter}
        visible={createFilterVisible}
      />
    </>
  );
};

export default FilterDropdown;
